import { useState } from 'react';
import {
  AppButton,
  AppChip,
  AppDateField,
  AppDivider,
  AppEmptyState,
  AppFab,
  AppFormDialog,
  AppPickerRow,
  AppReorderable,
  AppScreenScaffold,
  AppSearchField,
  AppSectionCard,
  AppSectionDivider,
  AppSectionRow,
  AppSwipeRow,
  AppTextField,
  AppTimeField,
  AppFilePicker,
  AppTopBar,
  AppUndoSnackbar,
  CaseTypeTile,
  Icon,
  IconButton,
  applyThemeMode,
  type CaseTypeKey,
  type ThemeMode,
} from '@core/designsystem';
import { StorageProbe } from './StorageProbe';
import './catalogue.css';

const TYPES: { key: CaseTypeKey; label: string }[] = [
  { key: 'court', label: 'Судебное дело' },
  { key: 'claim', label: 'Претензионная работа' },
  { key: 'consultation', label: 'Консультация' },
  { key: 'enforcement', label: 'Исп. производство' },
  { key: 'other', label: 'Другое' },
];

/**
 * Каталог компонентов дизайн-системы.
 *
 * До шага 18 он занимает место «Настроек»: это способ посмотреть все компоненты сразу
 * в обеих темах на живом iPhone, включая всплывающие поверхности — их в макете не видно.
 * Когда появятся настоящие настройки, каталог уйдёт вместе с проверкой хранилища.
 */
export function Catalogue({ onBack }: { onBack?: () => void }) {
  const [theme, setTheme] = useState<ThemeMode>('system');
  const [text, setText] = useState('Иванов — раздел имущества');
  const [notes, setNotes] = useState('');
  const [query, setQuery] = useState('');
  const [date, setDate] = useState('2026-09-14');
  const [clock, setClock] = useState('10:30');
  const [filter, setFilter] = useState('all');
  const [dialog, setDialog] = useState(false);
  const [undo, setUndo] = useState(false);
  const [order, setOrder] = useState(['Иск', 'Ходатайство', 'Судебный акт']);
  const [log, setLog] = useState('—');

  const setMode = (mode: ThemeMode) => {
    setTheme(mode);
    applyThemeMode(mode);
  };

  return (
    <AppScreenScaffold
      topBar={
        <AppTopBar
          title="Yatwa"
          subtitle="Каталог компонентов"
          root={!onBack}
          onBack={onBack}
          actions={<IconButton icon="tune" label="Фильтры" onClick={() => setLog('Фильтры')} />}
        />
      }
      overlay={
        <>
          {dialog ? (
            <AppFormDialog
              title="Новое дело"
              onDismiss={() => setDialog(false)}
              confirm={{ label: 'Создать', onClick: () => setDialog(false) }}
            >
              <AppTextField value={text} onChange={setText} label="Название" autoFocus />
              <AppPickerRow label="Тип" value="Судебное дело" onClick={() => setLog('Тип')} />
              <AppPickerRow label="Клиент" value="Не выбран" onClick={() => setLog('Клиент')} />
            </AppFormDialog>
          ) : null}
          {undo ? (
            <AppUndoSnackbar
              text="Документ удалён"
              onUndo={() => {
                setLog('Отменено');
                setUndo(false);
              }}
              onExpire={() => setUndo(false)}
            />
          ) : null}
        </>
      }
    >
      <div className="catalogue">
        <AppSectionCard title="Тема">
          <div className="catalogue__row">
            {(['system', 'light', 'dark'] as ThemeMode[]).map((mode) => (
              <AppChip
                key={mode}
                label={{ system: 'Системная', light: 'Светлая', dark: 'Тёмная' }[mode]}
                selected={theme === mode}
                onClick={() => setMode(mode)}
              />
            ))}
          </div>
        </AppSectionCard>

        <StorageProbe />

        <AppSectionCard title="Поля ввода">
          <div className="catalogue__stack">
            <AppTextField value={text} onChange={setText} label="Название дела" />
            <AppTextField value={notes} onChange={setNotes} label="Заметки" multiline />
            <AppSearchField value={query} onChange={setQuery} />
            <AppDateField value={date} onChange={setDate} label="Срок" />
            <AppTimeField value={clock} onChange={setClock} label="Время" />
          </div>
        </AppSectionCard>

        <AppSectionCard title="Плитки типов дела">
          <div className="catalogue__row catalogue__row--wrap">
            {TYPES.map((type) => (
              <span key={type.key} className="catalogue__tile">
                <CaseTypeTile type={type.key} />
                <span className="catalogue__caption">{type.label}</span>
              </span>
            ))}
          </div>
        </AppSectionCard>

        <AppSectionCard title="Фильтры">
          <div className="catalogue__row catalogue__row--wrap">
            {['all', 'active', 'paused', 'done'].map((key) => (
              <AppChip
                key={key}
                label={{ all: 'Все', active: 'Активные', paused: 'Приостановленные', done: 'Завершённые' }[key] ?? key}
                selected={filter === key}
                onClick={() => setFilter(key)}
              />
            ))}
          </div>
        </AppSectionCard>

        <AppSectionCard title="Раздел карточки" action={{ label: 'Все', onClick: () => setLog('Все') }}>
          <AppSectionRow icon="person" title="Иванов Иван" subtitle="+7 700 000 00 00" onClick={() => setLog('Клиент')} />
          <AppSectionDivider />
          <AppSectionRow icon="picture_as_pdf" title="Иск.pdf" subtitle="4 страницы · 320 КБ" />
          <AppSectionDivider />
          <AppSectionRow icon="add" title="Добавить описание" action onClick={() => setLog('Описание')} />
        </AppSectionCard>

        <AppSectionCard title="Строка со свайпом">
          <AppSwipeRow
            start={{ icon: 'document_scanner', label: 'Сканировать', onAction: () => setLog('Скан') }}
            end={{ icon: 'schedule', label: 'Срок', onAction: () => setLog('Срок') }}
            onLongPress={() => setLog('Долгое нажатие')}
          >
            <div className="catalogue__case-row">
              <CaseTypeTile type="court" />
              <span className="catalogue__case-texts">
                <span className="catalogue__case-title">Иванов — раздел имущества</span>
                <span className="catalogue__case-subtitle">Иванов И. · Судебное дело</span>
              </span>
            </div>
          </AppSwipeRow>
          <AppDivider withIcon />
          <p className="catalogue__note">Последнее действие: {log}</p>
        </AppSectionCard>

        <AppSectionCard title="Перетаскивание">
          <AppReorderable
            items={order}
            keyOf={(item) => item}
            onReorder={setOrder}
            renderRow={(item, handle) => (
              <div className="catalogue__case-row">
                <Icon name="description" size={22} />
                <span className="catalogue__case-texts">{item}</span>
                {handle}
              </div>
            )}
          />
        </AppSectionCard>

        <AppSectionCard title="Пустое состояние">
          <AppEmptyState
            icon="folder"
            text="Дел пока нет"
            action={<AppButton onClick={() => setDialog(true)}>Создать дело</AppButton>}
          />
        </AppSectionCard>

        <AppSectionCard title="Выбор файла">
          <div className="catalogue__row catalogue__row--wrap">
            <AppFilePicker
              label="Добавить файл"
              variant="text"
              multiple
              onFiles={(files) => setLog(`Выбрано файлов: ${files.length}`)}
            />
          </div>
        </AppSectionCard>

        <AppSectionCard title="Кнопки и плашки">
          <div className="catalogue__row catalogue__row--wrap">
            <AppButton onClick={() => setDialog(true)}>Диалог</AppButton>
            <AppButton variant="text" onClick={() => setUndo(true)}>
              Показать «Отменить»
            </AppButton>
            <AppButton variant="text" danger onClick={() => setLog('Удаление')}>
              Удалить
            </AppButton>
          </div>
        </AppSectionCard>
      </div>

      <AppFab icon="add" label="Добавить" onClick={() => setDialog(true)} />
    </AppScreenScaffold>
  );
}
